import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, HStack, Spacer, VStack, useTheme } from 'native-base';
import { find, first, isEmpty, isEqual, map, startCase, toLower } from 'lodash';

import { ConnectedDevicesIcon, CleanIcon, TargetIcon, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { AggPositionSlotDto, PositionAssigneeDto, ShiftDto } from '@pimm/services/lib/sms-workforce';
import { stringToDateLocal } from '@app/utils/date-formatter';
import { hexToRGBA } from '@app/utils/string-formatter';
import { useSiteTime } from '@app/features/store-core';
import { useKitchenPositionGuideLive } from '../context';
import { StarIcon } from '../icons';
import { CollapsibleTile } from './collapsible-tile';
import { TimeDisplay } from './time-display';
import KitchenPositionSlotTaskSection from './kitchen-position-slot-task-section';
import { TaskLists, TileMode } from '../types';
import { useCleaningTasksData } from '../hooks/use-cleaning-tasks-data';

type KitchenPositionSlotProps = {
  tileMode?: TileMode;
  hasCleaningTask?: boolean;
  positionSlot: AggPositionSlotDto;
  onPress?: (positionSlot: AggPositionSlotDto) => void;
};

const KitchenPositionSlot = ({
  tileMode = 'goals',
  positionSlot: { positionAssignee, positionJob, ...positionSlot },
  ...props
}: KitchenPositionSlotProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { translate } = useAppLocale();
  const siteTime = useSiteTime();
  const livePositionGuide = useKitchenPositionGuideLive();
  const [assignee, setAssignee] = useState<PositionAssigneeDto>();

  const color = positionJob?.primaryJob?.color ?? colors.gray[500];
  const isEmptyJobs = isEmpty(positionJob?.secondaryJobs);
  const positionalGoal = first(positionJob?.primaryJob?.positionalGoals);
  const goal = translate(positionalGoal?.description, positionalGoal?.translations);
  const showContent = tileMode !== 'collapsed';

  // Cleaning task buckets + 3-state icon color — always computed (icon shows in all modes)
  const { posBuckets, empBuckets, cleaningIconColor, positionTitle } = useCleaningTasksData(
    positionSlot.slotNumber ?? 0,
    assignee?.employeeId,
  );

  // Tile display derived from buckets — shown in cleaning-tasks mode
  const empActive = [...empBuckets.todayOverdue, ...empBuckets.todayPending, ...empBuckets.pastOverdue];
  const posActive = [...posBuckets.todayOverdue, ...posBuckets.todayPending];
  const MAX_TILE_TASKS = 3;
  const empShowMax = Math.min(empActive.length, 2);
  const posShowMax = Math.min(posActive.length, MAX_TILE_TASKS - empShowMax);
  const remainingCount = empActive.length + posActive.length - empShowMax - posShowMax;

  // pastOverdue is already in empActive, so no separate check needed there
  const empHasData = empActive.length > 0 || empBuckets.todayCompleted.length > 0;
  const posHasData = posActive.length > 0 || posBuckets.pastOverdue.length > 0 || posBuckets.todayCompleted.length > 0;
  const noDataAtAll = !empHasData && !posHasData;

  const empStats: TaskLists = {
    doneToday: empBuckets.todayCompleted.length,
    overdueCount: empBuckets.todayOverdue.length + empBuckets.pastOverdue.length,
    active: empActive,
  };
  const posStats: TaskLists = {
    doneToday: posBuckets.todayCompleted.length,
    overdueCount: posBuckets.todayOverdue.length + posBuckets.pastOverdue.length,
    active: posActive,
  };

  const handlePressSelect = () => {
    if (props.onPress)
      props.onPress({
        ...positionSlot,
        positionAssignee: assignee,
        positionJob: positionJob,
      });
  };

  useEffect(() => {
    let timeoutRef;

    const displayScheduledAssignee = (person?: PositionAssigneeDto, employeeSchedules?: ShiftDto[]) => {
      if (!person) {
        setAssignee(undefined);
        return undefined;
      }

      const timeNow = siteTime.today();
      const shift = find(employeeSchedules, shift => {
        if (person?.employeeId === shift.employee?.id) {
          const shiftStartTime = stringToDateLocal(shift.startTime);
          const shiftEndTime = stringToDateLocal(shift.endTime);
          return !!shiftStartTime && !!shiftEndTime && timeNow >= shiftStartTime && timeNow < shiftEndTime;
        }
        return false;
      });

      const startTime = stringToDateLocal(shift?.startTime ?? person.startTime);
      const endTime = stringToDateLocal(shift?.endTime ?? person.endTime);
      const isActive = startTime && endTime && timeNow >= startTime && timeNow < endTime;
      const nextAssignee = isActive
        ? {
            ...person,
            startTime: shift?.startTime ?? person.startTime,
            endTime: shift?.endTime ?? person.endTime,
          }
        : undefined;

      setAssignee(nextAssignee);

      const nearestQuarterHour = timeNow.toNearestQuarterHour();
      nearestQuarterHour.setSeconds(0, 0);

      return setTimeout(() => {
        displayScheduledAssignee(person, employeeSchedules);
      }, nearestQuarterHour.getTime() - timeNow.getTime());
    };

    timeoutRef = displayScheduledAssignee(positionAssignee, livePositionGuide.data?.employeeSchedules);
    return () => clearInterval(timeoutRef);
  }, [positionAssignee, livePositionGuide.data?.employeeSchedules]);

  return (
    <CollapsibleTile _container={{ minHeight: showContent ? '244px' : undefined }} isCollapsed={showContent} onPress={handlePressSelect}>
      {/* Primary Position Title */}
      <VStack rounded="xl" space={2} p={1.5} borderWidth={1} borderColor={hexToRGBA(color, 0.4)} bg={hexToRGBA(color, 0.2)}>
        <HStack alignItems="center" space={1.5} pr={0.5}>
          <Box rounded="md" alignContent="center" justifyContent="center" h={6} w={7} bg={hexToRGBA(color, 0.4)}>
            <Text size="lg" color="black" fontWeight={700} textAlign="center">
              {positionSlot.slotNumber ?? ''}
            </Text>
          </Box>

          <HStack flex={1} alignItems="center" h="full">
            {positionJob?.primaryJob?.id ? (
              <Box flex={1} pl={1}>
                <Text size="lg" fontWeight={700} color="black" lineHeight="xs" numberOfLines={2}>
                  {translate(positionJob?.primaryJob?.title, positionJob?.primaryJob.translations)}
                </Text>
              </Box>
            ) : (
              <Box rounded="md" justifyContent="center" px={2.5} w="full" h="full" bg="gray.700">
                <Text size="md" fontWeight={700} color="white" lineHeight="md">
                  {t('position_guide:no_position_assigned')}
                </Text>
              </Box>
            )}
          </HStack>

          {!!positionSlot.isOpsLeader && (
            <Box flex="none" rounded="full" p="2px" borderWidth={1} borderColor="white" bg="warning.300">
              <StarIcon size={3.5} />
            </Box>
          )}
        </HStack>

        {/* Assignee */}
        <HStack
          alignItems="flex-end"
          rounded="lg"
          py={1.5}
          px={2}
          minHeight="58px"
          borderWidth={1}
          borderColor={hexToRGBA(color, 0.5)}
          bg="white"
        >
          <VStack flex={3} space={1} justifyContent="center">
            {assignee ? (
              <>
                <Text size="lg" fontWeight={700} color="black" lineHeight="xs" numberOfLines={1} ellipsizeMode="tail">
                  {startCase(toLower(assignee.name))}
                </Text>
                {!!assignee?.startTime && !!assignee?.endTime && (
                  <TimeDisplay
                    dayBlock={siteTime.toDayBlockNow()}
                    startTime={assignee.startTime}
                    endTime={assignee.endTime}
                    keepAlive
                    _container={{ width: 'fit-content' }}
                  />
                )}
              </>
            ) : (
              <Box justifyContent="center" rounded="lg" px={5} h="44px" maxW="140px" bg="gray.900">
                <Text size="md" fontWeight={700} color="white" lineHeight="xs" textAlign="center" numberOfLines={2}>
                  {t('position_guide:no_employee_assigned')}
                </Text>
              </Box>
            )}
          </VStack>

          {/* Icon pill — goal icon inverted (gray=set, yellow=not set); clean icon 3-state */}
          <HStack flex={1} space={1} alignItems="center" justifyContent="flex-end">
            <TargetIcon size="16px" color={goal ? colors.gray[400] : colors.primary[500]} />
            <CleanIcon size="16px" color={cleaningIconColor} />
          </HStack>
        </HStack>
      </VStack>

      {/* Secondary Jobs */}
      <HStack flexWrap="wrap" alignItems="flex-start" minHeight={showContent ? 1 : 0} overflow="hidden">
        {!isEmptyJobs && (
          <Box mt={1.5} mr={1}>
            <ConnectedDevicesIcon size="14px" color="gray.600" />
          </Box>
        )}
        <HStack flex={1} flexWrap="wrap" pt={!isEmptyJobs ? 1.5 : 0}>
          {map(positionJob?.secondaryJobs, job => (
            <Box key={job.id} flex="none" pb={0.5}>
              <Box
                flex={1}
                rounded="md"
                justifyContent="center"
                mr={1.5}
                px={2}
                minH={5}
                borderWidth={1}
                borderColor={hexToRGBA(color, 0.5)}
                bg={hexToRGBA(color, 0.2)}
              >
                <Text size="sm" fontWeight={500} color="black" lineHeight="xs" numberOfLines={2}>
                  {translate(job.title, job.translations)}
                </Text>
              </Box>
            </Box>
          ))}
        </HStack>
      </HStack>

      {/* Daily Positional Goal
          Goal set    → gray icon + black text
          No goal set → yellow icon + warning.700 text + "Set a daily goal" */}
      {tileMode === 'goals' && (
        <VStack space={2} pt={1}>
          <Divider bg="gray.200" />

          <HStack space={1.5} alignItems="center" px={1.5}>
            <TargetIcon size="16px" color={goal ? colors.gray[700] : colors.primary[500]} />
            <Text size="md" color={goal ? 'gray.800' : 'warning.700'} fontWeight={700} lineHeight="xs">
              {t('position_guide:daily_positional_goals')}
            </Text>
          </HStack>

          <Box px={1.5} pb={2} maxHeight="80px">
            <Text size="md" fontWeight={500} color={goal ? 'black' : 'warning.700'} lineHeight="sm" ellipsizeMode="tail" numberOfLines={4}>
              {goal || t('position_guide:set_a_daily_goal')}
            </Text>
          </Box>
        </VStack>
      )}

      {/* Cleaning Tasks — two independent sections; both render on their own data presence.
          Section header CleanIcon tracks the same 3-state as the in-pill icon.
          "All caught up" removed — only "No tasks due yet" for empty state. */}
      {tileMode === 'cleaning-tasks' && (
        <VStack space={2} pt={1}>
          <Divider bg="gray.200" />

          <HStack space={1.5} alignItems="center" px={1.5}>
            <CleanIcon size="16px" color={cleaningIconColor} />
            <Text size="md" color="gray.800" fontWeight={700} lineHeight="xs">
              {t('position_guide:cleaning_tasks')}
            </Text>
          </HStack>

          {noDataAtAll ? (
            <Box px={1.5} pb={2}>
              <Text size="sm" fontWeight={500} color="gray.500" lineHeight="sm" italic>
                {t('position_guide:no_tasks_due_yet')}
              </Text>
            </Box>
          ) : (
            <VStack space={1.5} px={1.5} pb={2}>
              {/* Source 1: "Assigned to [FirstName]" — employee tasks (deduped against position tasks) */}
              {empHasData && assignee && (
                <KitchenPositionSlotTaskSection
                  stats={empStats}
                  showMax={empShowMax}
                  label={`${t('position_guide:assigned_to')} ${startCase(toLower(assignee.name)).split(' ')[0]}`}
                />
              )}

              {empHasData && posHasData && <Box w="full" borderBottomWidth={1} borderStyle="dashed" borderColor="gray.300" />}

              {/* Source 2: "[Position title] tasks" — position tasks */}
              {posHasData && (
                <KitchenPositionSlotTaskSection
                  stats={posStats}
                  showMax={posShowMax}
                  label={`${positionTitle ?? translate(positionJob?.primaryJob?.title, positionJob?.primaryJob?.translations)} ${t(
                    'position_guide:tasks',
                  )}`}
                />
              )}

              {remainingCount > 0 && (
                <Text size="sm" fontWeight={500} color="blue.500" lineHeight="xs" textTransform="lowercase">
                  +{remainingCount} {t('position_guide:more_task')}
                  {remainingCount > 1 ? 's' : ''} →
                </Text>
              )}
            </VStack>
          )}
        </VStack>
      )}
    </CollapsibleTile>
  );
};

const areEqual = (prevProps: KitchenPositionSlotProps, nextProps: KitchenPositionSlotProps) => {
  const { tileMode: prevTileMode, positionSlot: prevPositionSlot, hasCleaningTask: prevHasCleaningTask } = prevProps;
  const { tileMode, positionSlot, hasCleaningTask } = nextProps;

  return prevTileMode === tileMode && prevHasCleaningTask === hasCleaningTask && isEqual(prevPositionSlot, positionSlot);
};

export default memo(KitchenPositionSlot, areEqual);
